import React, { useState } from 'react';
import { Modal, Select, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { Typography, CircularProgress, TextField, Button, IconButton, FormControl, InputLabel } from '@mui/material';
import { useMutation } from '@apollo/client';
import './modal-edit.scss';
import { UPDATE_CLASS_TEMPLATE, GENERATE_CALENDARS } from '../../shared/graphql/branch';
import { Close, Check } from '@mui/icons-material';
import { showToast } from '../../shared/utils/toast';

interface ModalCreateClassProps {
  open: boolean;
  handleClose: () => void;
  availableClasses: any[];
  selectedDate: string;
  onScheduleCreated: () => void;
}

const ModalCreateClass: React.FC<ModalCreateClassProps> = ({ open, handleClose, availableClasses, selectedDate, onScheduleCreated }) => {
  const [selectedClassId, setSelectedClassId] = useState<number | ''>('');
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('10:00');
  const [capacity, setCapacity] = useState('20');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [updateClassTemplate] = useMutation(UPDATE_CLASS_TEMPLATE);
  const [generateCalendars] = useMutation(GENERATE_CALENDARS);

  const resetForm = () => {
    setSelectedClassId('');
    setStartTime('09:00');
    setEndTime('10:00');
    setCapacity('20');
  };

  const handleSave = () => {
    if (!selectedClassId) {
      showToast.warning('Por favor, selecciona una clase');
      return;
    }

    if (!startTime || !endTime) {
      showToast.warning('Por favor, completa los horarios');
      return;
    }

    if (startTime >= endTime) {
      showToast.warning('La hora de fin debe ser posterior a la hora de inicio');
      return;
    }

    setShowConfirmModal(true);
  };

  const handleConfirmSave = async () => {
    setIsSaving(true);
    setShowConfirmModal(false);

    try {
      const formattedDate = new Date(selectedDate).toISOString().split('T')[0];

      const result = await updateClassTemplate({
        variables: {
          classTemplate: [{
            id_template: null,
            start_time: startTime,
            end_time: endTime,
            days: null,
            capacity: parseInt(capacity, 10),
            enable: null,
            holidays: null,
            onlyHolidays: null,
            applyFriends: null,
            date_publication: formattedDate,
            class_id: selectedClassId
          }]
        }
      });

      if (result.data?.updateClassTemplate?.success) {
        const calendarResult = await generateCalendars({
          variables: {
            studioId: null,
            branchId: null,
            classId: selectedClassId,
            templateId: null
          }
        });

        if (calendarResult.data?.generateCalendars?.success) {
          showToast.success('¡Horario creado y calendarios generados exitosamente!');
        } else {
          showToast.warning('Horario creado, pero hubo un problema al generar los calendarios');
        }
        
        resetForm();
        handleClose();
        onScheduleCreated();
      } else {
        showToast.error(result.data?.updateClassTemplate?.message || 'Error al crear el horario');
      }
    } catch (error) {
      console.error("Error:", error);
      showToast.error('Error al crear el horario. Por favor, intenta de nuevo.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCloseModal = () => {
    if (!isSaving) {
      resetForm();
      handleClose();
    }
  };

  const getSelectedClassName = () => {
    const selectedClass = availableClasses.find(c => c.id === selectedClassId);
    return selectedClass?.name || '';
  };

  return (
    <>
      <Modal
        open={open}
        onClose={handleCloseModal}
        aria-labelledby="modal-create-schedule-title"
        aria-describedby="modal-create-schedule-description"
      >
        <div className="modalStyle">
          <IconButton
            onClick={handleCloseModal}
            disabled={isSaving}
            style={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <Close />
          </IconButton>

          <div className='modalContent' style={{ maxHeight: '80vh', overflowY: 'auto' }}>
            <Typography id="modal-create-schedule-title" variant="h5" component="h2" gutterBottom>
              Crear Nuevo Horario
            </Typography>

            <Typography variant="body2" color="textSecondary" gutterBottom>
              Fecha seleccionada: {new Date(selectedDate).toLocaleDateString('es-ES', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </Typography>

            <div style={{ marginTop: '20px' }}>
              <FormControl fullWidth variant="outlined" style={{ marginBottom: '20px' }} required>
                <InputLabel>Selecciona una clase</InputLabel>
                <Select
                  value={selectedClassId}
                  onChange={(e) => setSelectedClassId(e.target.value as number)}
                  disabled={isSaving || availableClasses.length === 0}
                  label="Selecciona una clase"
                >
                  {availableClasses.length === 0 ? (
                    <MenuItem disabled>No hay clases disponibles</MenuItem>
                  ) : (
                    availableClasses.map((classItem: any) => (
                      <MenuItem key={classItem.id} value={classItem.id}>
                        {classItem.name}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
              
              {availableClasses.length === 0 && (
                <Typography variant="body2" color="error" style={{ marginBottom: '20px' }}>
                  No hay clases disponibles en esta fecha. Por favor, selecciona otro día con clases existentes.
                </Typography>
              )}

              <Typography variant="subtitle1" gutterBottom>
                Configuración del Horario
              </Typography>

              <div style={{ display: 'flex', gap: '16px', marginBottom: '20px' }}>
                <TextField
                  label="Hora de inicio"
                  type="time"
                  fullWidth
                  variant="outlined"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  disabled={isSaving}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
                <TextField
                  label="Hora de fin"
                  type="time"
                  fullWidth
                  variant="outlined"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  disabled={isSaving}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </div>

              <TextField
                label="Capacidad"
                type="number"
                fullWidth
                variant="outlined"
                value={capacity}
                onChange={(e) => setCapacity(e.target.value)}
                disabled={isSaving}
                style={{ marginBottom: '20px' }}
                InputProps={{
                  inputProps: { min: 1 }
                }}
              />

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
                <Button 
                  variant="outlined" 
                  onClick={handleCloseModal}
                  disabled={isSaving}
                >
                  Cancelar
                </Button>
                <Button 
                  variant="contained" 
                  color="primary" 
                  onClick={handleSave}
                  disabled={isSaving}
                  startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : null}
                >
                  {isSaving ? 'Guardando...' : 'Crear Horario'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Modal>

      <Dialog 
        open={showConfirmModal} 
        onClose={() => !isSaving && setShowConfirmModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirmar Creación de Horario</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Estás seguro de que deseas crear un nuevo horario para la clase "{getSelectedClassName()}"?
          </Typography>
          <Typography style={{ marginTop: '10px' }}>
            <strong>Fecha:</strong> {new Date(selectedDate).toLocaleDateString('es-ES', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </Typography>
          <Typography>
            <strong>Horario:</strong> {startTime} - {endTime}
          </Typography>
          <Typography>
            <strong>Capacidad:</strong> {capacity} personas
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setShowConfirmModal(false)}
            disabled={isSaving}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleConfirmSave} 
            variant="contained" 
            color="primary"
            disabled={isSaving}
            startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <Check />}
          >
            {isSaving ? 'Creando...' : 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ModalCreateClass;

